<template>
  <div id="app" class="container-fluid">
    <full_head />

    <div class="main-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading labor progress...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchLaborRecords">Retry</button>
      </div>

      <!-- Content -->
      <div v-else class="row g-3">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-heartbeat"></i>
                Labor Summary
              </h3>
            </div>
            <div class="card-body">
              <div v-if="!records.length" class="no-data">
                No labor observations recorded yet. Add the first reading to start
                the partograph.
              </div>
              <div v-else class="gp-score">
                <div class="gp-item">
                  <span class="gp-label">Onset of Labor</span>
                  <span class="gp-value" style="font-size: 14px">{{
                    formatDateTime(onsetOfLabor)
                  }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Time in Labor</span>
                  <span class="gp-value" style="font-size: 16px">{{
                    timeInLabor
                  }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Latest Dilation</span>
                  <span class="gp-value"
                    >{{ latestRecord.cervical_dilation ?? "-" }} cm</span
                  >
                </div>
                <div class="gp-item">
                  <span class="gp-label">Latest FHR</span>
                  <span class="gp-value"
                    >{{ latestRecord.fetal_heart_rate ?? "-" }} bpm</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-chart-line"></i>
                Partograph Readings
              </h3>
              <button class="btn-edit-header" @click="openAddModal">
                <i class="fas fa-plus"></i>
                Add Observation
              </button>
            </div>
            <div class="card-body">
              <div v-if="!records.length" class="no-data">
                No readings recorded yet
              </div>
              <div v-else class="table-responsive">
                <table class="table-custom">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Dilation (cm)</th>
                      <th>Descent</th>
                      <th>Liquor</th>
                      <th>Moulding</th>
                      <th>Contractions</th>
                      <th>FHR</th>
                      <th>Pulse</th>
                      <th>BP</th>
                      <th>Temp</th>
                      <th>Oxytocin</th>
                      <th>Recorded By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="record in records" :key="record.id">
                      <td>{{ formatDateTime(record.recorded_at) }}</td>
                      <td>
                        <strong :class="dilationClass(record.cervical_dilation)">{{
                          record.cervical_dilation ?? "-"
                        }}</strong>
                      </td>
                      <td>{{ record.descent_of_head || "-" }}</td>
                      <td>{{ record.liquor || "-" }}</td>
                      <td>{{ record.moulding || "-" }}</td>
                      <td>
                        {{ record.contractions_per_10_min ?? "-" }}/10min
                        <span v-if="record.contraction_strength">
                          ({{ record.contraction_strength }})</span
                        >
                      </td>
                      <td>
                        <span :class="fhrClass(record.fetal_heart_rate)">{{
                          record.fetal_heart_rate ?? "-"
                        }}</span>
                      </td>
                      <td>{{ record.maternal_pulse ?? "-" }}</td>
                      <td>
                        <span v-if="record.bp_systolic && record.bp_diastolic">
                          {{ record.bp_systolic }}/{{ record.bp_diastolic }}
                        </span>
                        <span v-else>-</span>
                      </td>
                      <td>{{ record.maternal_temp ?? "-" }}</td>
                      <td>
                        <span v-if="record.oxytocin_given" class="badge-outcome outcome-live">
                          {{ record.oxytocin_drops_per_min || "Yes" }}
                        </span>
                        <span v-else class="text-muted">No</span>
                      </td>
                      <td>{{ record.recorded_by || "-" }}</td>
                      <td>
                        <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          class="btn-icon btn-icon-danger"
                          title="Delete"
                          @click="deleteRecord(record.id)"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <LaborEntryModal
      v-if="showModal"
      :visitId="visitId"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import LaborEntryModal from "./LaborEntryModal.vue";
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "MaternityLaborProgress",
  components: { full_head, LaborEntryModal },
  data() {
    return {
      loading: false,
      error: null,
      visitId: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  computed: {
    latestRecord() {
      return this.records.length ? this.records[this.records.length - 1] : {};
    },
    onsetOfLabor() {
      const withOnset = this.records.find((r) => r.onset_of_labor);
      return withOnset ? withOnset.onset_of_labor : null;
    },
    timeInLabor() {
      if (!this.onsetOfLabor) return "-";
      const start = new Date(this.onsetOfLabor);
      const end = new Date(
        this.latestRecord.recorded_at || Date.now()
      );
      const diffMs = end - start;
      if (isNaN(diffMs) || diffMs < 0) return "-";
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      return `${hours}h ${minutes}m`;
    },
  },
  mounted() {
    this.visitId = this.$route.params.visit_id;

    if (this.visitId) {
      this.fetchLaborRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchLaborRecords() {
      this.loading = true;
      this.error = null;

      try {
        const response = await generalApiRequest(
          `maternity_labor_records/visit/${this.visitId}`,
          "GET",
          {},
          1
        );

        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load labor records";
        }
      } catch (err) {
        console.error("Error fetching labor records:", err);
        this.error =
          "An error occurred while loading labor progress. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },

    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },

    async handleSaved() {
      this.closeModal();
      await this.fetchLaborRecords();
    },

    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this observation?")) return;

      try {
        const response = await generalApiRequest(
          `maternity_labor_records/${id}`,
          "DELETE",
          {},
          1
        );
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete observation");
        }
      } catch (error) {
        console.error("Error deleting labor record:", error);
        alert("Failed to delete observation. Please try again.");
      }
    },

    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    dilationClass(dilation) {
      if (!dilation) return "";
      if (dilation >= 10) return "status-positive";
      return "";
    },

    fhrClass(fhr) {
      if (!fhr) return "";
      if (fhr < 110 || fhr > 160) return "status-positive";
      return "status-negative";
    },
  },
};
</script>

<style scoped>
/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 10px;
  margin-top: 20px;
}

.loading-container i {
  font-size: 3rem;
  color: #667eea;
  margin-bottom: 1rem;
}

.loading-container p {
  color: #6c757d;
  font-size: 16px;
}

.error-container i {
  font-size: 3rem;
  color: #dc3545;
  margin-bottom: 1rem;
}

.error-container p {
  color: #dc3545;
  font-size: 16px;
  margin-bottom: 1.5rem;
}

.btn-retry {
  background: #667eea;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-retry:hover {
  background: #5a6fd6;
  transform: translateY(-1px);
}

/* Reset & Base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.container-fluid {
  padding: 12px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.main-content {
  margin-top: 12px;
}

/* Cards */
.card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header h3 i {
  color: #667eea;
  font-size: 18px;
}

.btn-edit-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 7px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  white-space: nowrap;
}

.btn-edit-header:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
}

.card-body {
  padding: 16px 20px;
}

/* GP Score (used here for the labor summary strip) */
.gp-score {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.gp-item {
  text-align: center;
  padding: 14px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  transition: transform 0.2s ease;
}

.gp-item:hover {
  transform: translateY(-2px);
}

.gp-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.gp-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

/* Custom Table */
.table-responsive {
  overflow-x: auto;
}

.table-custom {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.table-custom th {
  background: #f8f9fa;
  padding: 10px 8px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table-custom td {
  padding: 8px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  white-space: nowrap;
}

.table-custom tbody tr:hover {
  background: #f8f9fa;
}

.badge-outcome {
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.outcome-live {
  background: #d4edda;
  color: #155724;
}

.status-negative {
  color: #28a745;
  font-weight: 600;
}

.status-positive {
  color: #dc3545;
  font-weight: 600;
}

.text-muted {
  color: #6c757d;
  font-size: 11px;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 6px;
}

.btn-icon {
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  padding: 4px 6px;
  font-size: 13px;
}

.btn-icon-danger {
  color: #dc3545;
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    padding: 10px 14px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-header h3 {
    font-size: 15px;
  }

  .btn-edit-header {
    padding: 6px 14px;
    font-size: 11px;
    width: 100%;
    justify-content: center;
  }

  .card-body {
    padding: 12px 14px;
  }

  .gp-score {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .gp-item {
    padding: 12px 6px;
  }

  .gp-value {
    font-size: 20px;
  }

  .table-custom th,
  .table-custom td {
    padding: 6px;
    font-size: 11px;
  }
}

@media (max-width: 576px) {
  .container-fluid {
    padding: 8px;
  }
}
</style>
